<script setup lang="ts">
/**
 * A data access agreement, rendered from the markdown it is written in.
 * Rendering lives in `utils/agreement.ts`, shared with the copy printed for
 * signature, so what is read here and what is signed are the same text.
 *
 * The legend comes first and is short: the colours are how the document
 * tells a signer what they are accepting, and what is not settled yet.
 */
import { computed } from "vue";
import type { AgreementResponse } from "../api/admin";
import {
  fillPrintWindow,
  openPrintWindow,
  renderAgreementHtml,
} from "../utils/agreement";

const props = defineProps<{ agreement: AgreementResponse }>();

const html = computed(() => renderAgreementHtml(props.agreement.markdown));
const open = computed(() => props.agreement.meta.open);

function print(): void {
  const w = openPrintWindow();
  if (!w) return;
  const m = props.agreement.meta;
  fillPrintWindow(
    w,
    `${m.id || "Agreement"} v${m.version} — ${props.agreement.label}`,
    props.agreement.markdown,
  );
}
</script>

<template>
  <div class="agreement">
    <div class="agreement__bar">
      <p class="legend small">
        <span class="daa-assumed">Amber</span> — assumptions the signers accept
        ({{ open.assumed }}) · <span class="daa-review">red</span> — someone
        else's decision ({{ open.needs_review }})<template
          v-if="open.needs_data"
        >
          · <span class="daa-data">bold red</span> — missing, blocks signing ({{
            open.needs_data
          }})</template
        >
      </p>
      <button class="btn" @click="print">Print for signature</button>
    </div>
    <!-- eslint-disable-next-line vue/no-v-html — sanitised by DOMPurify -->
    <div class="agreement__body" v-html="html" />
  </div>
</template>

<style scoped>
.agreement {
  display: flex;
  flex-direction: column;
  gap: var(--space-3, 0.75rem);
}
.agreement__bar {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
}
.legend {
  flex: 1;
  margin: 0;
  padding: 0.5rem 0.75rem;
  border-left: 2px solid var(--color-border);
  background: var(--color-surface-alt);
  border-radius: var(--radius-sm);
}
.agreement__body {
  font-size: 0.9rem;
  line-height: 1.5;
}
.agreement__body :deep(h1) {
  font-size: 1.25rem;
}
.agreement__body :deep(h2) {
  font-size: 1.05rem;
  margin-top: 1.5rem;
  padding-top: 0.75rem;
  border-top: 1px solid var(--color-border);
}
.agreement__body :deep(h3) {
  font-size: 0.95rem;
}
.agreement__body :deep(table) {
  border-collapse: collapse;
  width: 100%;
  margin: 0.5rem 0;
  font-size: 0.85rem;
}
.agreement__body :deep(th),
.agreement__body :deep(td) {
  border: 1px solid var(--color-border);
  padding: 0.3rem 0.5rem;
  vertical-align: top;
  text-align: left;
}
.agreement__body :deep(blockquote) {
  margin: 0.5rem 0;
  padding: 0.25rem 0.75rem;
  border-left: 3px solid var(--color-border);
  color: var(--color-muted);
}
.agreement__body :deep(code) {
  font-size: 0.85em;
}
/* Not colour alone: amber is also italic, blocking red is also bold. */
.agreement :deep(.daa-assumed) {
  color: #b45f06;
  font-style: italic;
}
.agreement :deep(.daa-review) {
  color: #c00000;
}
.agreement :deep(.daa-data) {
  color: #c00000;
  font-weight: 700;
}
.muted {
  color: var(--color-muted);
}
.small {
  font-size: 0.82rem;
}
</style>
